/**
 * #299 critério 7 — moeda provável do cardápio pelo idioma/região do Menu.
 *
 * Usada SÓ quando nenhum símbolo de moeda foi lido (`currency` nulo no
 * rascunho): se a moeda provável do idioma difere da padrão do restaurante,
 * a revisão pede confirmação ao dono em vez de aplicar a moeda do país em
 * silêncio (caso nac.pdf: cardápio árabe de Riade num restaurante pt →
 * SAR, não EUR). Mapa PEQUENO e testado — pares que aparecem na importação
 * (árabe do Golfo, Levante e Magrebe + hebraico). A moeda confirmada vale
 * só para o import e os produtos dele; nunca muda o restaurante.
 */

/** Região do BCP-47 → moeda (ar-SA → SA → SAR). */
export const CURRENCY_BY_MENU_REGION: Record<string, string> = {
  SA: 'SAR',
  AE: 'AED',
  BH: 'BHD',
  KW: 'KWD',
  OM: 'OMR',
  QA: 'QAR',
  EG: 'EGP',
  MA: 'MAD',
}

/** Idioma-base sem região → moeda (árabe "nu" → Riyal saudita). */
export const CURRENCY_BY_MENU_LANGUAGE: Record<string, string> = {
  ar: 'SAR',
  he: 'ILS',
}

/** Região do BCP-47 (ar-SA → SA; zh-Hant-TW → TW); sem região → null. */
export function menuRegionOf(locale: string | null | undefined): string | null {
  const parts = (locale ?? '').trim().split(/[-_]/).slice(1)
  const region = parts.find((p) => /^[A-Za-z]{2}$/.test(p))
  return region ? region.toUpperCase() : null
}

/**
 * Moeda provável do cardápio pelo locale (com ou sem região). Desconhecido
 * → null — aí não há o que confirmar e vale a padrão do restaurante.
 */
export function currencyFromMenuLocale(locale: string | null | undefined): string | null {
  const raw = locale?.trim()
  if (!raw) return null
  const region = menuRegionOf(raw)
  if (region && CURRENCY_BY_MENU_REGION[region]) return CURRENCY_BY_MENU_REGION[region]
  const base = raw.split(/[-_]/)[0]?.toLowerCase() ?? ''
  return CURRENCY_BY_MENU_LANGUAGE[base] ?? null
}

export type CurrencyConfirmationInput = {
  /** moeda lida no Menu e gravada no import — null sem leitura */
  detected?: string | null
  /** moeda provável pelo idioma/região do Menu (`currencyFromMenuLocale`) */
  fromLocaleRegion?: string | null
  /** moeda padrão do restaurante (país > locale principal) */
  restaurantCurrency?: string | null
  /** #299: confirmação do dono já gravada (`menu_imports.currency_confirmed_at`) */
  confirmedAt?: number | null
}

/**
 * #299 critério 7 — a moeda pede confirmação do dono? `true` quando a moeda
 * DETETADA diverge da padrão do restaurante, ou quando NADA foi lido e a
 * região do idioma do Menu aponta para outra moeda (ar-SA → SAR num
 * restaurante EUR, caso nac.pdf). `confirmedAt` (coluna escrita só pelo
 * servidor no POST /currency) devolve sempre `false` — o dono já escolheu.
 */
export function currencyNeedsConfirmation(input: CurrencyConfirmationInput): boolean {
  if (input.confirmedAt !== null && input.confirmedAt !== undefined) return false
  const detected = input.detected?.trim().toUpperCase() || null
  const restaurant = input.restaurantCurrency?.trim().toUpperCase() || null
  if (detected) return restaurant !== null && detected !== restaurant
  const suggested = input.fromLocaleRegion?.trim().toUpperCase() || null
  return suggested !== null && restaurant !== null && suggested !== restaurant
}
